import type { Editor } from '@tiptap/core'
import type { Column, Cue } from '@rundown-studio/types'
import {
  CellDomUtils,
  type GridCell,
  type GridCellKey,
} from '../cell/cell.types'
import type { RectBounds } from '@/utils/events'

import {
  cellUploadActions,
  type CellUploadRegistry,
} from '../cell/upload/cell-upload'

import { proxy, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { upsertCell } from '../cell/cell.requests'
import { triggerToast } from '@/shared/ui/toast'

import * as logger from '../../../utils/logger'

type GridEditorSaveStatus =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; error: Error }

type GridEditorCellContext = {
  cellKey: GridCellKey
  cell: GridCell
  columnId: Column['id']
  cueId: Cue['id']
  clickCoords: {
    x: number
    y: number
    selectionRect: RectBounds | null
  } | null
}

type GridState = {
  // Columns
  editableColumns: Column['id'][]

  // Cells
  selectedCell: GridCellKey | null
  editingCell: GridCellKey | null

  // Editor
  editor: {
    cellContext: GridEditorCellContext | null
    /** Has user made any changes to the cell? */
    isDirty: boolean
    /** Was the cell updated somewhere else while user was editing? */
    isConflicting: boolean
    /** Should we show conflict resolution dialog before saving? */
    showConflictDialog: boolean
    getHTMLref: Editor['getHTML'] | null
    saveStatus: GridEditorSaveStatus
  }

  /** Registry tracking file uploads and progress for cells */
  cellUploadRegistry: CellUploadRegistry
}

const initialEditorState: GridState['editor'] = {
  cellContext: null,
  isDirty: false,
  isConflicting: false,
  showConflictDialog: false,
  getHTMLref: null,
  saveStatus: { status: 'idle' },
}

export const gridState: GridState = proxy({
  // Columns
  editableColumns: [],

  // Cells
  selectedCell: null,
  editingCell: null,

  // Active cell editor
  editor: deepClone(initialEditorState),

  cellUploadRegistry: {},
})

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(gridState, { name: 'gridState', enabled: true })

  // effect(() => {
  //   logger.log('[Editor] isDirty', gridState.editor.isDirty)
  // })

  // effect(() => {
  //   logger.log('[Editor] isConflicting', gridState.editor.isConflicting)
  // })
}

// #endregion

export const gridStateActions = {
  setSelectedCell: (cellKey: GridCellKey) => {
    if (gridState.editingCell) return
    gridState.selectedCell = cellKey
  },
  resetSelectedCell: () => {
    gridState.selectedCell = null
  },
  setEditingCell: (cellKey: GridCellKey, context: GridEditorCellContext) => {
    gridState.editingCell = cellKey
    gridState.editor.cellContext = context
  },
  resetEditingCell: () => {
    gridState.editingCell = null
    gridStateActions.editor.resetEditor()
  },
  /**
   * Move focus to cell (ie. <CellFrame/>)
   */
  focusCell(cellKey: GridCellKey) {
    const cellElm = CellDomUtils.queryValue('data-cell-key', cellKey)

    cellElm?.focus({ preventScroll: true })
  },
  /**
   * Namespace for editor related actions
   */
  editor: {
    getEditorContent() {
      return gridState.editor.getHTMLref?.() || ''
    },
    resetEditor() {
      gridState.editor = deepClone(initialEditorState)
    },
    /**
     * Attempts to save the current cell edits.
     *
     * This is the primary action for the save button and `Cmd+Enter`.
     */
    save() {
      logger.info('[Editor action]: save')
      const { editor } = gridState

      // Discard if nothing was changed
      if (!editor.isDirty) {
        gridStateActions.editor.discard()
        return
      }

      // If conflict detect, stop and defer to user
      if (editor.isConflicting) {
        editor.showConflictDialog = true
        return
      }

      // Commit save
      gridStateActions.editor._commitSave()
    },

    /**
     * Force‑saves the current cell edits, bypassing the conflict check.
     *
     * Used when the user explicitly chooses "Overwrite" in the conflict dialog.
     * Dismisses the conflict dialog and commits the edits unconditionally.
     */
    saveForce() {
      logger.info('[Editor action]: saveForce')
      const { editor } = gridState

      // Abort if nothing changed
      if (!editor.isDirty) return

      // Bypass conflict check, but close dialog in case open
      editor.showConflictDialog = false

      // Commit save
      gridStateActions.editor._commitSave()
    },

    /**
     * Gracefully closes the editor, saving if there are unsaved changes.
     *
     * Intended for click‑outside (blur) handlers.
     * If the cell is dirty, it delegates to save (which handles conflicts).
     * If clean, it discards immediately.
     */
    saveOrDiscard() {
      logger.info('[Editor action]: saveOrDiscard')
      const { editor } = gridState

      if (editor.isDirty) {
        gridStateActions.editor.save()
      } else {
        gridStateActions.editor.discard()
      }
    },

    /**
     * Discards any pending changes and exits editing mode.
     */
    discard() {
      logger.info('[Editor action]: discard')

      const cellKey = gridState.editingCell
      if (!cellKey) return

      gridStateActions.focusCell(cellKey)
      gridStateActions.resetEditingCell()
    },

    /**
     * Internal: performs the actual save operation.
     *
     * @private
     */
    _commitSave() {
      logger.info('[Editor action]: _commitSave')
      gridState.editor.saveStatus = { status: 'pending' }

      const { cellContext } = gridState.editor
      if (!cellContext) return

      const { cell, columnId, cueId } = cellContext

      const contentPayload = {
        content: {
          text: gridStateActions.editor.getEditorContent(),
        },
      }

      upsertCell(cell, columnId, cueId, contentPayload)
        .then(() => {
          gridState.editor.saveStatus = { status: 'success' }

          gridStateActions.resetEditingCell()
        })
        .catch((err) => {
          logger.error('[GridState] Save failed:', err)

          triggerToast({
            type: 'fail',
            title: `Save failed.`,
            body: 'Please try again or copy your changes and refresh the page.',
          })

          gridState.editor.saveStatus = {
            status: 'error',
            error: err instanceof Error ? err : new Error('Save failed'),
          }
        })
    },
  },
  /**
   * Namespace for upload related actions
   */
  cellUpload: cellUploadActions,
}

export function useGridSnap() {
  return useSnapshot(gridState)
}
